import { defaultTimeTrackerForm } from "app/containers/Features/containers/TimeSheet/redux/initialState";
import { selectActiveTimerForm } from "app/containers/Features/containers/TimeSheet/redux/selectors";
import { timeSheetActions } from "app/containers/Features/containers/TimeSheet/redux/slice";
import { ISelectedTicketDetails } from "app/containers/Features/containers/TimeSheet/types";
import { selectTimeTracker } from "app/redux/selector";
import { actions } from "app/redux/slice";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

/** Format total seconds as "H:MM:SS" */
export function formatSeconds(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function getStartAndEndDate(hours: number, minutes: number) {
  const endTime = new Date().toISOString();
  const startTime = new Date(
    new Date().getTime() - hours * 60 * 60 * 1000 - minutes * 60 * 1000,
  ).toISOString();
  return { startTime, endTime };
}

export function getDayDifferenceNumber(isoDate: string): number {
  const input = new Date(isoDate);
  const now = new Date();

  const inputDateOnly = new Date(
    input.getFullYear(),
    input.getMonth(),
    input.getDate(),
  );

  const nowDateOnly = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );

  const diffMs = nowDateOnly.getTime() - inputDateOnly.getTime();

  return Math.abs(Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

export function formatSecondsDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0) parts.push(`${s}s`);

  return parts.length > 0 ? parts.join(" ") : "0s";
}

export function formatTimeLabel(date: Date): string {
  return date
    .toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
}

const STORAGE_KEY = "time_tracker";
const FORM_STORAGE_KEY = "time_tracker_form";

export type TimeOption = {
  label: string; // UI display (10:30 AM)
  value: string; // HH:mm (for input control)
  iso: string; // full ISO string (for backend)
};

export function generateEndTimeSlots(
  startTime: Date,
  intervalMinutes: number = 15,
): TimeOption[] {
  const slots: TimeOption[] = [];

  const start = new Date(startTime);

  const minutes = start.getMinutes();
  const remainder = minutes % intervalMinutes;

  if (remainder !== 0) {
    start.setMinutes(minutes + (intervalMinutes - remainder));
  } else {
    start.setMinutes(minutes + intervalMinutes);
  }

  start.setSeconds(0, 0);

  const endOfDay = new Date(startTime);
  endOfDay.setHours(23, 59, 59, 999);

  while (start <= endOfDay) {
    const slot = new Date(start);

    const label = formatTimeLabel(slot);

    const value = `${String(slot.getHours()).padStart(2, "0")}:${String(
      slot.getMinutes(),
    ).padStart(2, "0")}`;

    const iso = slot.toISOString();

    slots.push({ label, value, iso });

    // move forward
    start.setMinutes(start.getMinutes() + intervalMinutes);
  }

  return slots;
}

export function generateFullDayTimeSlots(
  baseDate: Date,
  intervalMinutes: number = 15,
): TimeOption[] {
  const slots: TimeOption[] = [];

  const start = new Date(baseDate);
  start.setHours(0, 0, 0, 0); // 🔥 Start from 12:00 AM

  const totalSlots = (24 * 60) / intervalMinutes;

  for (let i = 0; i < totalSlots; i++) {
    const slot = new Date(start);
    slot.setMinutes(i * intervalMinutes);

    const label = formatTimeLabel(slot); // 12:00 am format

    const value = `${String(slot.getHours()).padStart(2, "0")}:${String(
      slot.getMinutes(),
    ).padStart(2, "0")}`;

    const iso = slot.toISOString();

    slots.push({ label, value, iso });
  }

  return slots;
}

export function getTimeDuration(startISO: string, endISO: string) {
  const start = new Date(startISO).getTime();
  const end = new Date(endISO).getTime();

  if (end <= start) {
    return { hours: 0, minutes: 0 };
  }

  const diffInSeconds = Math.floor((end - start) / 1000);

  const hours = Math.floor(diffInSeconds / 3600);
  const remaining = diffInSeconds % 3600;

  const minutes = remaining === 0 ? 0 : Math.max(1, Math.round(remaining / 60));

  return { hours, minutes };
}

export function useTimeTracker() {
  const dispatch = useDispatch();
  const { startTime, elapsed, isRunning } = useSelector(selectTimeTracker);
  const activeTimerForm = useSelector(selectActiveTimerForm);

  // Restore activeTimerForm from localStorage on mount (persist across refresh)
  // Only do this once on mount
  useEffect(() => {
    const storedForm = localStorage.getItem(FORM_STORAGE_KEY);
    if (storedForm) {
      try {
        const parsed = JSON.parse(storedForm);
        dispatch(timeSheetActions.setActiveTimerForm(parsed));
      } catch {}
    }
  }, [dispatch]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialRender = useRef(true);

  const [seconds, setSeconds] = useState(elapsed);

  // 🔁 Restore from localStorage
  useEffect(() => {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      dispatch(actions.restore(parsed));
    }
  }, [dispatch]);

  // 💾 Persist (skip first render so we don't overwrite before restore)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ startTime, elapsed, isRunning }),
    );
  }, [startTime, elapsed, isRunning]);

  // 💾 Persist activeTimerForm to localStorage whenever it changes while running
  useEffect(() => {
    if (isRunning) {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(activeTimerForm));
    }
  }, [activeTimerForm, isRunning]);

  // ⏱ Live time
  const getLiveSeconds = () => {
    if (isRunning && startTime) {
      return elapsed + Math.floor((Date.now() - startTime) / 1000);
    }
    return elapsed;
  };

  // 🔄 Tick
  useEffect(() => {
    if (!isRunning || !startTime) return;

    const tick = () => {
      setSeconds(getLiveSeconds());
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, startTime, elapsed]);

  // Sync when stopped
  useEffect(() => {
    if (!isRunning) {
      setSeconds(elapsed);
    }
  }, [isRunning, elapsed]);

  // =====================
  // ACTIONS
  // =====================

  const handleTimerStop = useCallback(
    (newTask?: ISelectedTicketDetails) => {
      const hours = Math.floor(seconds / 3600);
      const remaining = seconds % 3600;
      const minutes =
        remaining === 0 ? 0 : Math.max(1, Math.round(remaining / 60));

      const storedForm = localStorage.getItem(FORM_STORAGE_KEY);
      const baseForm = storedForm ? JSON.parse(storedForm) : activeTimerForm;

      const updatedForm = {
        ...baseForm,
        endTime: new Date().toISOString(),
        hours,
        minutes,
      };

      dispatch(actions.stop());
      dispatch(timeSheetActions.setActiveTimerForm(updatedForm));
      dispatch(
        timeSheetActions.effortActualRequest({
          callback: ({ form, response }) => {
            dispatch(
              timeSheetActions.addTimeSheetEntryLocally({
                timeSheetId: response.effort.date,
                newEntry: {
                  _id: response._id,
                  effort: response.effort,
                  ticketId: form.selectedTicketDetails._id,
                  projectId: form.selectedTicketDetails.projectId,
                  isLock: false,
                  billableHours: form.billableHours || false,
                  ticketTitle: form.selectedTicketDetails.ticketName,
                  ticketType: form.selectedTicketDetails.typeIcon || "",
                  ticketTypeName: "",
                  ticketTypeIcon: form.selectedTicketDetails.typeIcon || "",
                  ticketAssignee: "",
                },
              }),
            );

            if (newTask) {
              console.log("if trigger");

              dispatch(
                timeSheetActions.setActiveTimerForm({
                  ...defaultTimeTrackerForm,
                  selectedTicketDetails: newTask,
                }),
              );
            } else {
              console.log("else trigger");

              dispatch(
                timeSheetActions.setActiveTimerForm(defaultTimeTrackerForm),
              );
            }
          },
          isActiveTimer: true,
        }),
      );
      handleReset();
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, seconds, activeTimerForm],
  );

  const handleTimerStart = useCallback(
    ({
      customStart,
      newTask,
    }: {
      customStart?: Date;
      newTask?: ISelectedTicketDetails;
    }) => {
      if (
        isRunning &&
        activeTimerForm.selectedTicketDetails._id === newTask?._id
      ) {
        handleTimerStop();
        return;
      }
      const time = customStart?.getTime() ?? Date.now();

      if (isRunning) {
        handleTimerStop(newTask);
      }

      dispatch(actions.start({ startTime: time }));
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, isRunning, handleTimerStop],
  );

  const handleReset = useCallback(() => {
    dispatch(actions.reset());
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(FORM_STORAGE_KEY);
  }, [dispatch]);

  // 🔥 Toggle (your requirement)
  const toggle = useCallback(
    ({
      customStart,
      newTask,
    }: { customStart?: Date; newTask?: ISelectedTicketDetails } = {}) => {
      if (isRunning) {
        handleTimerStop();
      } else {
        handleTimerStart({ customStart, newTask });
      }
    },
    [isRunning, handleTimerStart, handleTimerStop],
  );

  // 🧠 Update start time while running
  const updateStartTime = (newStart: Date) => {
    const newElapsed = Math.floor((Date.now() - newStart.getTime()) / 1000);

    dispatch(
      actions.restore({
        startTime: newStart.getTime(),
        elapsed: newElapsed > 0 ? newElapsed : 0,
        isRunning: true,
      }),
    );
  };

  // ⏰ Schedule future start
  const scheduleStart = (future: Date) => {
    const delay = future.getTime() - Date.now();

    if (delay <= 0) {
      handleTimerStart({ customStart: future });
      return;
    }

    setTimeout(() => handleTimerStart({ customStart: future }), delay);
  };

  // Combine Date and Time
  function combineDateAndTime(date: string, time: string): string {
    // Ensure time has seconds
    const fullTime = time.split(":").length === 2 ? `${time}:00` : time;

    // Combine into local date-time
    const dateTimeString = `${date}T${fullTime}`;

    const dateObj = new Date(dateTimeString);

    return dateObj.toISOString();
  }

  return {
    seconds,
    isRunning,

    handleTimerStart,
    handleTimerStop,
    reset: handleReset,
    toggle,

    updateStartTime,
    scheduleStart,
    combineDateAndTime,
    generateEndTimeSlots,
    generateFullDayTimeSlots,

    formattedTime: formatSeconds(seconds),
    formattedDuration: formatSecondsDuration(seconds),
  };
}
